import {
  ITributosFaturamentoDataSource,
  ITributosFaturamentoDetalhesCofinsDataSource,
  ITributosFaturamentoDetalhesPISDataSource,
  ITributosFaturamentoDetalhesSimplesDataSource
} from "@/features/contabil/dashboard/widgets/TributosFaturamento/types";
import {
  ITributosFaturamentoEmpresasResponse,
  ITributosFaturamentoResponse
} from "../requests/types";
import EmpresaMapper from "@/features/contabil/common/EmpresaMapperAdapter";

type tributosPagos = {
  cofins: number;
  pis: number;
  simples: number;
}[];

export default class TributosFaturamento {
  constructor(readonly response: ITributosFaturamentoResponse | undefined) {}

  private certificadoInvalido(
    empresa: ITributosFaturamentoEmpresasResponse
  ): boolean {
    return empresa.certificadoInvalido;
  }

  private handlePendencia = <T>(
    empresa: ITributosFaturamentoEmpresasResponse,
    value: T
  ) => {
    if (this.certificadoInvalido(empresa)) return "**";

    return value || "*";
  };

  private handleEmpresa = (
    empresa: ITributosFaturamentoEmpresasResponse,
    value: string | undefined,
    isInvalid: boolean
  ) => {
    const empresaPossuiPendencia = this.certificadoInvalido(empresa);
    if (empresaPossuiPendencia) return this.handlePendencia(empresa, value);

    return isInvalid ? "*" : value || "*";
  };

  formatToTable(): ITributosFaturamentoDataSource[] {
    return (
      this.response?.empresas.map((empresa) => {
        const isInvalidPisOuCofins = empresa.dctf != undefined;

        return {
          empresa: EmpresaMapper.toEntity(empresa),
          cofins: this.handleEmpresa(
            empresa,
            empresa.dctf?.diferencaCofins?.toString(),
            !isInvalidPisOuCofins
          ),
          pis: this.handleEmpresa(
            empresa,
            empresa.dctf?.diferencaPis?.toString(),
            !isInvalidPisOuCofins
          ),
          simples: this.handleEmpresa(
            empresa,
            empresa.simples?.diferenca?.toString(),
            isInvalidPisOuCofins
          )
        };
      }) || []
    );
  }

  private getTributosPagos(
    tributos: ITributosFaturamentoEmpresasResponse[]
  ): tributosPagos {
    return (
      tributos.map((empresa) => ({
        cofins: empresa.dctf?.diferencaCofins || 0,
        pis: empresa.dctf?.diferencaPis || 0,
        simples: empresa.simples?.diferenca || 0
      })) || []
    );
  }

  private tributosPagosPorCategoria(
    results: tributosPagos,
    categoria: "isPositive" | "isNegative"
  ): tributosPagos {
    const handleOperation: Record<
      typeof categoria,
      (value: number) => boolean
    > = {
      isNegative: (value) => value < 0,
      isPositive: (value) => value > 0
    };

    return results.map((item) => {
      let cofins = 0;
      let pis = 0;
      let simples = 0;

      if (handleOperation[categoria](item.cofins)) cofins = item.cofins;
      if (handleOperation[categoria](item.pis)) pis = item.pis;
      if (handleOperation[categoria](item.simples)) simples = item.simples;

      return {
        cofins: cofins || 0,
        pis: pis || 0,
        simples: simples || 0
      };
    });
  }

  totalPaid(): number {
    const results = this.getTributosPagos(this.response?.empresas || []);

    const total = results.reduce(
      (acc, curr) => ({
        simples: acc.simples + curr.simples,
        confins: acc.confins + curr.cofins,
        pis: acc.pis + curr.pis
      }),
      { confins: 0, pis: 0, simples: 0 }
    );

    return total.confins + total.pis + total.simples;
  }

  private sumTributos(tributos: tributosPagos): number {
    const total = tributos.reduce(
      (acc, curr) => ({
        simples: acc.simples - curr.simples,
        cofins: acc.cofins - curr.cofins,
        pis: acc.pis - curr.pis
      }),
      { cofins: 0, pis: 0, simples: 0 }
    );

    return total.cofins + total.pis + total.simples;
  }

  positiveTotalPaid(): number {
    const results = this.getTributosPagos(this.response?.empresas || []);

    const justPositive = this.tributosPagosPorCategoria(results, "isPositive");

    const total = this.sumTributos(justPositive);

    return Math.abs(total);
  }

  negativeTotalPaid(): number {
    const results = this.getTributosPagos(this.response?.empresas || []);

    const justNegatives = this.tributosPagosPorCategoria(results, "isNegative");

    const total = this.sumTributos(justNegatives);

    return Math.abs(total) * -1;
  }

  detalhesFaturamentoPIS(): ITributosFaturamentoDetalhesPISDataSource[] {
    if (!this.response?.empresas) return [];

    return (
      this.response.empresas.map((empresa) => ({
        empresa: EmpresaMapper.toEntity(empresa),
        dctf: {
          pisPagar: this.handlePendencia(
            empresa,
            empresa.dctf?.declaradoPisDctf?.toString()
          )
        },
        diffPIS: this.handlePendencia(
          empresa,
          empresa.dctf?.diferencaPis?.toString()
        ),
        efd: {
          pisPagar: this.handlePendencia(
            empresa,
            empresa.dctf?.declaradoPis?.toString()
          )
        }
      })) || []
    );
  }

  detalhesFaturamentoSimples(): ITributosFaturamentoDetalhesSimplesDataSource[] {
    if (!this.response?.empresas) return [];

    return (
      this.response.empresas.map((empresa) => ({
        empresa: EmpresaMapper.toEntity(empresa),
        das: {
          pago: this.handlePendencia(empresa, empresa.simples?.pago?.toString())
        },
        pdgas: {
          declarado: this.handlePendencia(
            empresa,
            empresa.simples?.devido?.toString()
          )
        },
        simples: {
          diferenca: this.handlePendencia(
            empresa,
            empresa.simples?.diferenca?.toString()
          )
        }
      })) || []
    );
  }

  detalhesFaturamentoCofins(): ITributosFaturamentoDetalhesCofinsDataSource[] {
    if (!this.response?.empresas) return [];

    return (
      this.response.empresas.map((empresa) => ({
        empresa: EmpresaMapper.toEntity(empresa),
        dctf: {
          pagar: this.handlePendencia(
            empresa,
            empresa.dctf?.declaradoCofinsDctf?.toString()
          )
        },
        efd: {
          pagar: this.handlePendencia(
            empresa,
            empresa.dctf?.declaradoCofins?.toString()
          )
        },
        efdxdctf: {
          diferenca: this.handlePendencia(
            empresa,
            empresa.dctf?.diferencaCofins?.toString()
          )
        }
      })) || []
    );
  }

  hasNull(): boolean {
    return (
      this.response?.empresas.some(
        (empresa) =>
          !empresa.dctf?.diferencaCofins ||
          empresa.dctf?.diferencaPis ||
          empresa.simples?.diferenca
      ) || false
    );
  }
}
